import type { ObjectsService, PropertyValue } from '../services/ObjectsService'
import { formatGqlUserMessage } from '@/common/errors/gql-error-utils'
import type { CreateObjectInput } from '@/generated/graphql.ts'
import type { Dayjs } from 'dayjs'
import { BuiltInPropertyNames } from '@/objects/gql/object-type-gql-utils.ts'
import type { PropertyDefinitionModel, ObjectTypeModel } from '@/core/gql/gql-models-override.ts'
import { isClientId } from '@/common/network/gql-types.ts'
import { isCorePropDef } from '@/object-types/services/ObjectTypesService.ts'
import type { IObjectFormController } from '../object-form/IObjectFormController'

export class CreateObjectController implements IObjectFormController {
  public readonly propDefIdToValue = new Map<number, PropertyValue>()
  private type: ObjectTypeModel
  private closeFn!: () => void

  constructor(private readonly objectsService: ObjectsService) {}

  public init(type: ObjectTypeModel, close: () => void): void {
    this.type = type
    this.closeFn = close
    for (const propDef of type.propertyDefinitions.filter((x) => !x.isMeta)) {
      this.propDefIdToValue.set(propDef.id, isCorePropDef(propDef) ? '' : null)
    }
  }

  public namePropDef(): PropertyDefinitionModel {
    return this.type.propertyDefinitions.find((x) => x.name === BuiltInPropertyNames.Name)!
  }

  public descrPropDef(): PropertyDefinitionModel {
    return this.type.propertyDefinitions.find((x) => x.name === BuiltInPropertyNames.Description)!
  }

  public getCustomPropDefs(): PropertyDefinitionModel[] {
    return this.type.propertyDefinitions.filter((x) => !x.isMeta && !isCorePropDef(x))
  }

  public getMetaPropDefs(): PropertyDefinitionModel[] {
    return []
  }

  public getPropValue(propDefId: number): PropertyValue {
    return this.propDefIdToValue.get(propDefId) ?? null
  }

  public onPropValueChange(propDefId: number, value: PropertyValue): void {
    this.propDefIdToValue.set(propDefId, value)
  }

  public async onSubmit(): Promise<string | undefined> {
    const { error } = await this.objectsService.createObject(this.toCreateInput())
    if (error) return formatGqlUserMessage(error.message)
    this.closeFn()
    return undefined
  }

  public onClose(): void {
    this.closeFn()
  }

  public toCreateInput(): CreateObjectInput {
    return {
      typeId: this.type.id,
      propertyValues: Array.from(this.propDefIdToValue.entries())
        .filter((entry) => !isClientId(entry[0]))
        .map(([definitionId, value]) => ({
          definitionId,
          value: this.propertyValueToString(value),
        })),
    }
  }

  private propertyValueToString(value: PropertyValue): string | null {
    if (value == null) return null
    if (this.isDayjs(value)) return value.toISOString()
    if (Array.isArray(value)) return JSON.stringify(value)
    return String(value)
  }

  private isDayjs(val: unknown): val is Dayjs {
    return !!val && typeof (val as Dayjs).isValid === 'function'
  }
}
